import React, { useState, useMemo, useEffect, useCallback } from 'react';
import supabase from '../lib/supabaseClient';

// The preview renders the REAL marketing signup page in an iframe rather than a
// local copy of it, so there is only ever one implementation of that page.
const PREVIEW_TARGET = 'https://www.getsurfox.com';
const PREVIEW_SRC = `${PREVIEW_TARGET}/partner-signup?preview=1`;

// Echoes back what a limit field will actually store, so a typo is visible
// before the invite is created rather than after.
function limitFieldHint(raw, noun) {
  const v = (raw || '').trim();
  if (!v) return 'Blank uses the standard tier limit.';
  if (/^unlimited$/i.test(v)) return `Stores unlimited ${noun}.`;
  const n = parseInt(v, 10);
  if (!Number.isFinite(n) || String(n) !== v) {
    return `"${v}" is not a number or the word unlimited, so it will be ignored.`;
  }
  return `Stores ${n.toLocaleString()} ${noun}.`;
}

export default function PartnerInvite() {
  const [formData, setFormData] = useState({
    target_role: 'partner_admin',
    partner_type: 'franchise',
    partner_name: '',
    partner_email: '',
    partner_company: '',
    one_time_payment: '',
    recurring_payment: '',
    recurring_interval: 'month',
    business_admin_discount: '20',
    max_business_admins: '',
    unlimited_business_admins: false,
    // Custom plan limits
    custom_sms_limit: '',
    custom_campaigns_limit: '',
    custom_knowledge_docs_limit: '',
    custom_ai_learning_history_limit: '',
    legal_notice: ''
  });
  const [inviteUrl, setInviteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewConnected, setPreviewConnected] = useState(false);

  const isPartnerAdmin = formData.target_role === 'partner_admin';

  // Calculate suggested pricing based on SMS limit
  const suggestedPricing = useMemo(() => {
    const smsLimit = parseInt(formData.custom_sms_limit) || 0;
    if (smsLimit === 0) return null;

    // Pricing tiers for reference
    // Starter: 2,000 SMS @ $97 = $0.0485/SMS
    // Growth: 10,000 SMS @ $497 = $0.0497/SMS  
    // Scale: 50,000 SMS @ $1,997 = $0.0399/SMS

    let monthlyPrice;
    let tierNote;

    if (smsLimit <= 2000) {
      // At or below Starter
      monthlyPrice = 97;
      tierNote = 'Starter tier';
    } else if (smsLimit <= 10000) {
      // Between Starter and Growth - interpolate
      const ratio = (smsLimit - 2000) / (10000 - 2000);
      monthlyPrice = Math.round(97 + (ratio * (497 - 97)));
      tierNote = 'Between Starter & Growth';
    } else if (smsLimit <= 40000) {
      // Between Growth and Scale - interpolate
      const ratio = (smsLimit - 10000) / (40000 - 10000);
      monthlyPrice = Math.round(497 + (ratio * (1997 - 497)));
      tierNote = 'Between Growth & Scale';
    } else {
      // Above Scale - use Scale rate ($0.0399/SMS)
      monthlyPrice = Math.round(smsLimit * 0.0399);
      tierNote = 'Scale rate ($0.04/SMS)';
    }

    // Round to nearest $50 for cleaner pricing
    monthlyPrice = Math.round(monthlyPrice / 50) * 50;
    if (monthlyPrice < 97) monthlyPrice = 97; // Minimum

    const annualPrice = Math.round(monthlyPrice * 12 * 0.85); // 15% annual discount

    return {
      monthly: monthlyPrice,
      annual: annualPrice,
      tierNote,
      annualSavings: (monthlyPrice * 12) - annualPrice
    };
  }, [formData.custom_sms_limit]);

  // Apply suggested price to form
  const applySuggestedPrice = (type) => {
    if (!suggestedPricing) return;
    
    if (type === 'monthly') {
      setFormData({
        ...formData,
        recurring_payment: suggestedPricing.monthly.toString(),
        recurring_interval: 'month'
      });
    } else {
      setFormData({
        ...formData,
        recurring_payment: suggestedPricing.annual.toString(),
        recurring_interval: 'year'
      });
    }
  };

  // A field can say a number, or the word "unlimited". Unlimited is stored as
  // null because that is what the rest of the app already reads as "no cap"
  // (BillingPage renders unlimitedText for null/undefined). Blank is different:
  // it means "leave this one on the standard tier limit", so we omit the key.
  const parseLimitField = (raw) => {
    const v = (raw || '').trim();
    if (!v) return undefined;
    if (/^unlimited$/i.test(v)) return null;
    // Whole digits only, so "25abc" is rejected rather than silently becoming 25.
    if (!/^\d+$/.test(v)) return undefined;
    return parseInt(v, 10);
  };

  // Build custom_plan_limits object only if any limit is set
  const buildCustomPlanLimits = useCallback(() => {
    const limits = {};
    let hasCustomLimits = false;

    if (formData.custom_sms_limit) {
      limits.sms_limit = parseInt(formData.custom_sms_limit);
      hasCustomLimits = true;
    }
    const campaigns = parseLimitField(formData.custom_campaigns_limit);
    if (campaigns !== undefined) {
      limits.campaigns_limit = campaigns;
      hasCustomLimits = true;
    }
    const knowledgeDocs = parseLimitField(formData.custom_knowledge_docs_limit);
    if (knowledgeDocs !== undefined) {
      limits.knowledge_docs_limit = knowledgeDocs;
      hasCustomLimits = true;
    }
    if (formData.custom_ai_learning_history_limit) {
      limits.ai_learning_history_limit = parseInt(formData.custom_ai_learning_history_limit);
      hasCustomLimits = true;
    }

    return hasCustomLimits ? limits : null;
  }, [formData]);

  // Shape an unsaved invite exactly like a partner_invites row so the real
  // signup page can render it without knowing it came from a draft.
  const buildPreviewInvite = useCallback(() => ({
    id: 'preview',
    invite_code: 'PREVIEW',
    partner_type: formData.partner_type,
    partner_name: formData.partner_name,
    partner_email: formData.partner_email || 'their-email@company.com',
    partner_company: formData.partner_company || 'Their Company',
    one_time_payment: formData.one_time_payment ? parseInt(formData.one_time_payment) : null,
    recurring_payment: formData.recurring_payment ? parseInt(formData.recurring_payment) : null,
    recurring_interval: formData.recurring_payment ? formData.recurring_interval : null,
    business_admin_discount: isPartnerAdmin ? parseInt(formData.business_admin_discount) : 0,
    max_business_admins: isPartnerAdmin
      ? (formData.unlimited_business_admins ? null : (formData.max_business_admins ? parseInt(formData.max_business_admins) : null))
      : null,
    status: 'pending',
    // Mirrors the 30-day window create-partner-invite stamps on insert.
    expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    target_role: formData.target_role,
    trial_days: 0,
    // Local data URL: the file is not uploaded to storage until you create.
    logo_url: logoPreview || null,
    custom_plan_limits: buildCustomPlanLimits(),
    legal_notice: formData.legal_notice.trim() || null
  }), [formData, isPartnerAdmin, logoPreview, buildCustomPlanLimits]);

  // The iframe announces itself, then we hand it the draft. Nothing sensitive
  // goes in the URL and we only answer the real marketing origin.
  useEffect(() => {
    if (!showPreview) return;
    const onMessage = (e) => {
      if (e.origin !== PREVIEW_TARGET) return;
      if (e.data?.type !== 'surfox:preview-ready') return;
      setPreviewConnected(true);
      e.source?.postMessage(
        { type: 'surfox:preview-invite', invite: buildPreviewInvite() },
        PREVIEW_TARGET
      );
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [showPreview, buildPreviewInvite]);

  const openPreview = () => {
    setPreviewConnected(false);
    setShowPreview(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: { session } } = await supabase.auth.getSession();

      const tenantId = user.app_metadata.tenant_id;

      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      // Validate at least one payment amount (convert to numbers first)
      const oneTimeAmount = formData.one_time_payment ? parseInt(formData.one_time_payment) : 0;
      const recurringAmount = formData.recurring_payment ? parseInt(formData.recurring_payment) : 0;

      if (oneTimeAmount === 0 && recurringAmount === 0) {
        throw new Error('Please enter at least one payment amount');
      }

      const customPlanLimits = buildCustomPlanLimits();

      // Upload logo if provided
      let logoUrl = null;
      if (logoFile) {
        const fileExt = logoFile.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${tenantId}/${fileName}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('partner-logos')
          .upload(filePath, logoFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          throw new Error(`Failed to upload logo: ${uploadError.message}`);
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('partner-logos')
          .getPublicUrl(filePath);

        logoUrl = publicUrl;
      }

      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-partner-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          target_role: formData.target_role,
          partner_type: formData.partner_type,
          partner_name: formData.partner_name,
          partner_email: formData.partner_email,
          partner_company: formData.partner_company,
          one_time_payment: formData.one_time_payment ? parseInt(formData.one_time_payment) : null,
          recurring_payment: formData.recurring_payment ? parseInt(formData.recurring_payment) : null,
          recurring_interval: formData.recurring_payment ? formData.recurring_interval : null,
          business_admin_discount: isPartnerAdmin ? parseInt(formData.business_admin_discount) : 0,
          max_business_admins: isPartnerAdmin
            ? (formData.unlimited_business_admins ? null : (formData.max_business_admins ? parseInt(formData.max_business_admins) : null))
            : null,
          invited_by: tenantId,
          custom_plan_limits: customPlanLimits,
          logo_url: logoUrl,
          legal_notice: formData.legal_notice.trim() || null
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to create invite');
      }
      
      setInviteUrl(result.invite_url);

      // Reset logo
      setLogoFile(null);
      setLogoPreview(null);

      // Reset form
      setFormData({
        target_role: 'partner_admin',
        partner_type: 'franchise',
        partner_name: '',
        partner_email: '',
        partner_company: '',
        one_time_payment: '',
        recurring_payment: '',
        recurring_interval: 'month',
        business_admin_discount: '20',
        max_business_admins: '',
        unlimited_business_admins: false,
        custom_sms_limit: '',
        custom_campaigns_limit: '',
        custom_knowledge_docs_limit: '',
        custom_ai_learning_history_limit: '',
        legal_notice: ''
      });
    } catch (error) {
      console.error('Error creating invite:', error);
      alert('Failed to create invite: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteUrl);
    alert('Invite link copied!');
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid image file (PNG, JPG, WEBP, or SVG)');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('Logo file must be less than 5MB');
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Custom Deal Builder</h1>
        <p className="text-gray-600 mt-2">Create custom invitations with flexible pricing, roles, and limits</p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Account Type */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Account Type</h2>
          <div className="flex gap-4">
            <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.target_role === 'partner_admin' 
                ? 'border-orange-500 bg-orange-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input
                type="radio"
                name="target_role"
                value="partner_admin"
                checked={formData.target_role === 'partner_admin'}
                onChange={(e) => setFormData({...formData, target_role: e.target.value})}
                className="sr-only"
              />
              <div className="font-semibold text-gray-900">Partner Admin</div>
              <div className="text-sm text-gray-600 mt-1">Can invite and manage business admins with discounted pricing</div>
            </label>
            <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.target_role === 'business_admin' 
                ? 'border-orange-500 bg-orange-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input
                type="radio"
                name="target_role"
                value="business_admin"
                checked={formData.target_role === 'business_admin'}
                onChange={(e) => setFormData({...formData, target_role: e.target.value})}
                className="sr-only"
              />
              <div className="font-semibold text-gray-900">Business Admin</div>
              <div className="text-sm text-gray-600 mt-1">Standalone account with custom pricing (no sub-accounts)</div>
            </label>
          </div>
        </div>

        {/* Partner Type - Only show for partner_admin */}
        {isPartnerAdmin && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Partner Type</h2>
            <select
              required
              value={formData.partner_type}
              onChange={(e) => setFormData({...formData, partner_type: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="franchise">Franchise Partner</option>
              <option value="white_label">White Label Partner</option>
              <option value="enterprise">Enterprise Direct</option>
            </select>
          </div>
        )}

        {/* Contact Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">{isPartnerAdmin ? 'Partner' : 'Customer'} Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                type="text"
                required
                value={formData.partner_name}
                onChange={(e) => setFormData({...formData, partner_name: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                required
                value={formData.partner_email}
                onChange={(e) => setFormData({...formData, partner_email: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="john@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
              <input
                type="text"
                required
                value={formData.partner_company}
                onChange={(e) => setFormData({...formData, partner_company: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Company Inc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo (Optional)</label>
              {!logoPreview ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-400 transition-colors">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                    onChange={handleLogoChange}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload" className="cursor-pointer">
                    <div className="text-4xl mb-2">🖼️</div>
                    <div className="text-sm text-gray-600 mb-1">Click to upload company logo</div>
                    <div className="text-xs text-gray-500">PNG, JPG, WEBP, or SVG • Max 5MB</div>
                  </label>
                </div>
              ) : (
                <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
                  <div className="flex items-center gap-4">
                    <img src={logoPreview} alt="Logo preview" className="h-16 w-16 object-contain rounded" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{logoFile.name}</div>
                      <div className="text-xs text-gray-500">{(logoFile.size / 1024).toFixed(1)} KB</div>
                    </div>
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-100 rounded transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                💡 Logo will appear on the customer's personalized signup page
              </p>
            </div>
          </div>
        </div>

        {/* Custom Plan Limits */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-2">Custom Plan Limits</h2>
          <p className="text-sm text-gray-600 mb-4">Leave blank to use standard tier limits. Fill in to override specific limits.</p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMS Limit (monthly)
              </label>
              <input
                type="number"
                min="0"
                value={formData.custom_sms_limit}
                onChange={(e) => setFormData({...formData, custom_sms_limit: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="e.g. 25000"
              />
              <p className="text-xs text-gray-500 mt-1">Standard: 1K / 10K / 50K</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaigns Limit
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.custom_campaigns_limit}
                onChange={(e) => setFormData({...formData, custom_campaigns_limit: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder='e.g. 25 or "unlimited"'
              />
              <p className="text-xs text-gray-500 mt-1">
                Standard: 3 / 10 / unlimited. {limitFieldHint(formData.custom_campaigns_limit, 'campaigns')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Knowledge Docs Limit
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.custom_knowledge_docs_limit}
                onChange={(e) => setFormData({...formData, custom_knowledge_docs_limit: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder='e.g. 50 or "unlimited"'
              />
              <p className="text-xs text-gray-500 mt-1">
                Standard: 0 / 5 / 100. {limitFieldHint(formData.custom_knowledge_docs_limit, 'knowledge docs')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AI Learning History Limit
              </label>
              <input
                type="number"
                min="0"
                value={formData.custom_ai_learning_history_limit}
                onChange={(e) => setFormData({...formData, custom_ai_learning_history_limit: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="e.g. 500"
              />
              <p className="text-xs text-gray-500 mt-1">Standard: 0 / 100 / 1000</p>
            </div>
          </div>

          {/* Pricing Calculator */}
          {suggestedPricing && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">💡 Suggested Pricing for {parseInt(formData.custom_sms_limit).toLocaleString()} SMS/month</h3>
              <p className="text-xs text-blue-700 mb-3">{suggestedPricing.tierNote}</p>
              <div className="grid grid-cols-2 gap-4">
                <div 
                  className="bg-white p-3 rounded-lg border border-blue-200 cursor-pointer hover:border-blue-400 transition-colors"
                  onClick={() => applySuggestedPrice('monthly')}
                >
                  <div className="text-xs text-gray-500 uppercase">Monthly</div>
                  <div className="text-2xl font-bold text-gray-900">${suggestedPricing.monthly.toLocaleString()}</div>
                  <div className="text-xs text-blue-600 mt-1">Click to apply</div>
                </div>
                <div 
                  className="bg-white p-3 rounded-lg border border-blue-200 cursor-pointer hover:border-blue-400 transition-colors"
                  onClick={() => applySuggestedPrice('annual')}
                >
                  <div className="text-xs text-gray-500 uppercase">Annual (15% off)</div>
                  <div className="text-2xl font-bold text-gray-900">${suggestedPricing.annual.toLocaleString()}</div>
                  <div className="text-xs text-green-600 mt-1">Save ${suggestedPricing.annualSavings.toLocaleString()}/yr</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Legal Notice */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-2">Legal Notice</h2>
          <p className="text-sm text-gray-600 mb-4">
            Optional. Shows as a bullet under "Your Plan Includes" on this customer's signup page.
            Use it to set expectations you want them to have agreed to before they pay.
          </p>
          <textarea
            rows={6}
            value={formData.legal_notice}
            onChange={(e) => setFormData({...formData, legal_notice: e.target.value})}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="e.g. Carriers require a warm-up period on any new sending number. Sending is limited to 1,000/day in week one..."
          />
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-gray-500">Leave blank to show nothing.</p>
            <p className="text-xs text-gray-400">{formData.legal_notice.length} characters</p>
          </div>
        </div>

        {/* Billing */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Billing</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                One-time Payment (optional)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  value={formData.one_time_payment}
                  onChange={(e) => setFormData({...formData, one_time_payment: e.target.value})}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="5000"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Initial setup or onboarding fee</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recurring Payment (optional)
              </label>
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <span className="absolute left-4 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.recurring_payment}
                    onChange={(e) => setFormData({...formData, recurring_payment: e.target.value})}
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="500"
                  />
                </div>
                <select
                  value={formData.recurring_interval}
                  onChange={(e) => setFormData({...formData, recurring_interval: e.target.value})}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  disabled={!formData.recurring_payment}
                >
                  <option value="month">per month</option>
                  <option value="year">per year</option>
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-1">Ongoing platform access fee</p>
            </div>

            {!formData.one_time_payment && !formData.recurring_payment && (
              <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded p-3">
                ⚠️ At least one payment amount is required
              </p>
            )}
          </div>
        </div>

        {/* Business Admin Pricing - Only show for partner_admin */}
        {isPartnerAdmin && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Business Admin Pricing</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount for their business admins
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={formData.business_admin_discount}
                    onChange={(e) => setFormData({...formData, business_admin_discount: e.target.value})}
                    className="flex-1"
                  />
                  <div className="w-20 text-center">
                    <span className="text-2xl font-bold text-orange-600">{formData.business_admin_discount}%</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Business admins invited by this partner get {formData.business_admin_discount}% off standard pricing
                </p>
                <div className="mt-2 text-sm text-gray-600 space-y-1">
                  <div>Starter ($97) → ${Math.round(97 * (1 - formData.business_admin_discount / 100))}/mo</div>
                  <div>Growth ($497) → ${Math.round(497 * (1 - formData.business_admin_discount / 100))}/mo</div>
                  <div>Scale ($1,997) → ${Math.round(1997 * (1 - formData.business_admin_discount / 100))}/mo</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max business admins allowed
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min="1"
                    value={formData.max_business_admins}
                    onChange={(e) => setFormData({...formData, max_business_admins: e.target.value})}
                    disabled={formData.unlimited_business_admins}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                    placeholder="10"
                  />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.unlimited_business_admins}
                      onChange={(e) => setFormData({
                        ...formData, 
                        unlimited_business_admins: e.target.checked,
                        max_business_admins: e.target.checked ? '' : formData.max_business_admins
                      })}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Unlimited</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.unlimited_business_admins 
                    ? "Partner can invite unlimited business admins" 
                    : "Maximum number of business admins this partner can invite"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={openPreview}
            className="px-6 py-4 rounded-lg font-semibold text-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Preview
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-orange-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Creating Invite...' : 'Create Custom Invite'}
          </button>
        </div>
      </form>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex flex-col p-4 md:p-8">
          <div className="bg-white rounded-t-lg px-4 py-3 flex items-center justify-between border-b border-gray-200">
            <div>
              <div className="font-semibold text-gray-900">Signup page preview</div>
              <div className="text-xs text-gray-500">
                {previewConnected
                  ? 'Live render of the real signup page. Nothing has been created yet.'
                  : 'Loading the real signup page...'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-900 text-white hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
          <iframe
            key={showPreview ? 'open' : 'closed'}
            src={PREVIEW_SRC}
            title="Partner signup preview"
            className="flex-1 w-full bg-white rounded-b-lg border-0"
          />
          {!previewConnected && (
            <div className="bg-amber-50 border-t border-amber-200 text-amber-800 text-xs px-4 py-2 rounded-b-lg">
              If this stays blank, the marketing site has not been deployed with preview support yet.
            </div>
          )}
        </div>
      )}

      {/* Success Message */}
      {inviteUrl && (
        <div className="mt-8 bg-green-50 border-2 border-green-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <h2 className="text-lg font-semibold text-green-900">Invite Created!</h2>
          </div>
          <p className="text-sm text-green-800 mb-4">Send this link to complete their signup:</p>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={inviteUrl}
              className="flex-1 px-4 py-3 border-2 border-green-300 rounded-lg bg-white font-mono text-sm"
            />
            <button
              onClick={copyToClipboard}
              className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Copy Link
            </button>
          </div>
        </div>
      )}
    </div>
  );
}